import { useTranslation } from "react-i18next";
import { createStyles } from "antd-style";

import { usePublicCompany } from "@av/apps/common-apps/platform/services/companies/public-company";
import { JobPostDetail } from "@av/apps/recruitment/ats-common/components/jobs/JobPostDetail";
import { JobPostDetailDrawer } from "@av/apps/recruitment/ats-common/components/jobs/JobPostDetailDrawer";
import { getLocalizedJobPostContent } from "@av/apps/recruitment/ats-common/localized-job-post-content";
import type { PublicJobPostDetailsModel } from "@av/apps/recruitment/ats-common/types";
import { Card } from "@av/components/atoms/Card";
import { CompanyLogo } from "@av/components/molecules/logo/CompanyLogo";
import { RenderDocAsHTML } from "@av/components/organisms/editor/rich-text-editor/RenderDocAsHTML";
import { useLocaleStore } from "@av/utils/hooks/useLocaleStore";

type ViewJobDescriptionDrawerProps = {
  companyId: string;
  jobPost: PublicJobPostDetailsModel;
  open: boolean;
  onClose: () => void;
};

const useStyles = createStyles(({ css }) => ({
  card: css`
    border-radius: 0;
    border: none;
    padding: 0;
    & .ant-card-body {
      padding: 16px;
    }
    & .ant-card-head {
      padding-left: 16px;
    }
  `,
}));

export function ViewJobDescriptionDrawer({
  companyId,
  jobPost,
  open,
  onClose,
}: ViewJobDescriptionDrawerProps) {
  const { t } = useTranslation();
  const { styles } = useStyles();
  const { locale } = useLocaleStore();
  const { data: publicCompany } = usePublicCompany(companyId);
  const description = getLocalizedJobPostContent(jobPost?.descriptions, locale);

  return (
    <JobPostDetailDrawer
      testIdPrefix="public-job-post"
      open={open}
      onClose={onClose}
      title={t("recruitment.jobApplication.jobDetails")}
    >
      <JobPostDetail
        isPublic
        jobPost={jobPost}
        testIdPrefix="public-job-post"
        jobContentAdditionalItems={
          <CompanyLogo size="small" testIdPrefix="public-job-post" company={publicCompany} />
        }
        jobPostContent={
          <Card
            title={t("recruitment.jobApplication.description")}
            rootClassName={styles.card}
            testIdPrefix="public-job-post-description-card"
          >
            <RenderDocAsHTML doc={description} testIdPrefix="public-job-post-description" />
          </Card>
        }
      />
    </JobPostDetailDrawer>
  );
}
